import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { User, Lock, Save, ArrowLeft, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { authApi } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { displayRole } from '@/lib/roleDisplay';
import { passwordChangeMessage, type KcActionState } from '@/lib/kcAction';
import { useLocation, useNavigate } from 'react-router-dom';

export default function Account() {
  const { user, changePassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Fetch full profile (firstName/lastName) from Keycloak
  const { data: profile } = useQuery({
    queryKey: ['auth-profile'],
    queryFn: () => authApi.getProfile(),
    staleTime: 30_000,
  });

  const [firstName, setFirstName] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);
  const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // #998: what Keycloak's password form answered, which AuthCallback passes
  // back as router state. Kept here and cleared from history, so a reload
  // does not announce it again.
  const [passwordStatus] = useState(() => (location.state as KcActionState | null)?.kcActionStatus ?? null);
  const [redirecting, setRedirecting] = useState(false);
  useEffect(() => {
    if ((location.state as KcActionState | null)?.kcActionStatus) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);
  const passwordMsg = passwordStatus ? passwordChangeMessage(passwordStatus) : null;

  const displayFirst = firstName ?? profile?.user?.firstName ?? '';
  const displayLast = lastName ?? profile?.user?.lastName ?? '';

  const profileMutation = useMutation({
    mutationFn: (data: { firstName?: string; lastName?: string }) => authApi.updateProfile(data),
    onSuccess: () => {
      setProfileMsg({ type: 'success', text: 'Profile updated successfully' });
      setFirstName(null);
      setLastName(null);
      queryClient.invalidateQueries({ queryKey: ['auth-profile'] });
    },
    onError: () => {
      setProfileMsg({ type: 'error', text: 'Failed to update profile' });
    },
  });

  const handleProfileSave = () => {
    setProfileMsg(null);
    profileMutation.mutate({ firstName: displayFirst, lastName: displayLast });
  };

  // Leaves the app for Keycloak's own form. oidc-client-ts's redirect never
  // settles once the browser is navigating, so settling at all means it did
  // not leave (no UserManager, or the discovery fetch failed).
  const handlePasswordChange = () => {
    setRedirecting(true);
    changePassword().finally(() => setRedirecting(false));
  };

  const inputClass =
    'w-full px-3 py-2 bg-jeket-black-elevated border border-jeket-gold/20 rounded-lg text-sm text-jeket-silver-bright placeholder-jeket-silver-muted/50 focus:outline-none focus:border-jeket-gold/50 transition-colors';
  const readOnlyClass =
    'w-full px-3 py-2 bg-jeket-black-elevated/50 border border-jeket-black-elevated rounded-lg text-sm text-jeket-silver-muted cursor-not-allowed';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-1.5 rounded-lg hover:bg-jeket-black-elevated transition-colors text-jeket-silver-muted hover:text-jeket-silver-bright"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-semibold text-jeket-silver-bright">Account</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Card */}
        <div className="bg-jeket-black-surface border border-jeket-black-elevated rounded-xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <User size={18} className="text-jeket-gold" />
            <h2 className="text-base font-medium text-jeket-silver-bright">Profile</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs text-jeket-silver-muted mb-1">Email</label>
              <input type="text" readOnly value={user?.email ?? ''} className={readOnlyClass} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-jeket-silver-muted mb-1">First Name</label>
                <input
                  type="text"
                  value={displayFirst}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={inputClass}
                  placeholder="First name"
                />
              </div>
              <div>
                <label className="block text-xs text-jeket-silver-muted mb-1">Last Name</label>
                <input
                  type="text"
                  value={displayLast}
                  onChange={(e) => setLastName(e.target.value)}
                  className={inputClass}
                  placeholder="Last name"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-jeket-silver-muted mb-1">Role</label>
              {/* The role in the acting org, not the token's legacy tier (#860). */}
              <input
                type="text"
                readOnly
                value={user ? displayRole(user).label : ''}
                title={user ? displayRole(user).title : undefined}
                className={readOnlyClass}
                data-testid="account-role"
              />
            </div>

            {user?.orgId && (
              <div>
                <label className="block text-xs text-jeket-silver-muted mb-1">Organization</label>
                <input type="text" readOnly value={user.orgId} className={readOnlyClass} />
              </div>
            )}

            {profileMsg && (
              <p className={cn('text-xs', profileMsg.type === 'success' ? 'text-status-healthy' : 'text-jeket-error')}>
                {profileMsg.text}
              </p>
            )}

            <button
              onClick={handleProfileSave}
              disabled={profileMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-jeket-gold/20 text-jeket-gold rounded-lg text-sm font-medium hover:bg-jeket-gold/30 transition-colors disabled:opacity-50"
            >
              <Save size={14} />
              {profileMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* Change Password Card: Keycloak's own form, never the BFF (#998) */}
        <div className="bg-jeket-black-surface border border-jeket-black-elevated rounded-xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <Lock size={18} className="text-jeket-gold" />
            <h2 className="text-base font-medium text-jeket-silver-bright">Change Password</h2>
          </div>

          <div className="space-y-4">
            <p className="text-sm text-jeket-silver-muted">
              You'll set a new password on the Jeket sign-in page, which may ask for your current one first, and then
              come back here.
            </p>

            {passwordMsg && (
              <p
                className={cn(
                  'text-xs',
                  passwordMsg.type === 'success'
                    ? 'text-status-healthy'
                    : passwordMsg.type === 'error'
                      ? 'text-jeket-error'
                      : 'text-jeket-silver-muted',
                )}
                data-testid="password-status"
              >
                {passwordMsg.text}
              </p>
            )}

            <button
              onClick={handlePasswordChange}
              disabled={redirecting}
              className="flex items-center gap-2 px-4 py-2 bg-jeket-gold/20 text-jeket-gold rounded-lg text-sm font-medium hover:bg-jeket-gold/30 transition-colors disabled:opacity-50"
            >
              <ExternalLink size={14} />
              {redirecting ? 'Opening sign-in...' : 'Change Password'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
